import React from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { InCardContext } from '~/components/ui/card/Card';
import { ModalCloseButton } from '~/components/ui/modal';

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  /** Replaces the title text in the header (the title still names the dialog when this is omitted). */
  header?: React.ReactNode;
  /** md = max-w-sm, lg = max-w-lg, xl = max-w-xl, full = the whole screen, with Modal's header and close button. */
  width?: 'md' | 'lg' | 'xl' | 'full';
  testId?: string;
  titleId?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

const widthClasses = { md: 'max-w-sm', lg: 'max-w-lg', xl: 'max-w-xl', full: 'max-w-none' };

/** Right-hand tray for inspecting or filtering without leaving the page (DESIGN.md §10.2). */
export function Drawer({
  isOpen,
  onClose,
  title,
  header,
  width = 'lg',
  testId,
  titleId,
  children,
  footer,
}: DrawerProps) {
  const full = width === 'full';
  return (
    <Dialog open={isOpen} onClose={onClose} className="fixed inset-0 z-40" data-testid={testId}>
      <DialogBackdrop className="fixed inset-0 bg-black/40 dark:bg-white/10" />
      {/* Portals to `document.body`, outside the protected layout's `dark:text-white/90`;
          the panel carries the default so un-classed text isn't black on the dark tray. */}
      <DialogPanel
        className={`fixed right-0 flex w-full flex-col bg-white text-gray-800 shadow-xl dark:bg-gray-900 dark:text-white/90 ${widthClasses[width]} inset-y-0`}
      >
        <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-6 py-4 dark:border-gray-800">
          {header ?? (
            <DialogTitle
              as="h2"
              id={titleId}
              className={
                full
                  ? 'text-xl font-bold text-gray-800 dark:text-white/90'
                  : 'text-lg font-semibold text-gray-800 dark:text-white/90'
              }
            >
              {title}
            </DialogTitle>
          )}
          {header && (
            <DialogTitle as="h2" id={titleId} className="sr-only">
              {title}
            </DialogTitle>
          )}
          <ModalCloseButton onClick={onClose} />
        </div>
        {/* Context crosses the portal: a drawer opened from inside a Card is not itself in one, so its tables keep their frame. */}
        <InCardContext.Provider value={false}>
          <div className="flex-1 overflow-y-auto p-6">{children}</div>
        </InCardContext.Provider>
        {footer && (
          <div
            className={`border-t border-gray-200 dark:border-gray-800 ${full ? 'px-6 py-4' : 'p-6'}`}
          >
            {footer}
          </div>
        )}
      </DialogPanel>
    </Dialog>
  );
}
